var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc pie chart
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Sector, Polyline } from '../../element';
import { LegendType } from '../../interface/legend';
import { SeriesModel } from '../../model';
import { isPercent, getPercentOrNumberValue, degToRad, getPolar, normalizeAngle, getPercentListWithRemainderMethod, getPercentListWithMinSizeRatio, } from '../../utils/math';
import { eventGraph } from '../../action';
import { getSectorLabelPosition } from '../../utils/textLayout';
import normalizeToArray from '../../utils/normalizeToArray';
import pieLabelLayout from './labelLayout';
import merge from '../../utils/merge';
import { createRichText } from '../../rich';
import { registerChart } from '../index';
function getDefaultLabelOption() {
    return {
        show: true,
        position: 'outside',
        distance: 30,
        distanceToConnectLine: 2,
        rotate: null,
        alignTo: 'none',
        margin: 24,
        offsetX: 0,
        offsetY: 0,
        color: null,
        fontSize: 12,
        formatter: function (point) { return point.name; },
    };
}
/**
 * pie model
 */
var PieSeriesModel = /** @class */ (function (_super) {
    __extends(PieSeriesModel, _super);
    function PieSeriesModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'pie';
        _this.legendType = LegendType.DATUM_LEGEND;
        return _this;
    }
    PieSeriesModel.prevProcessOption = function (option) {
        option.series.forEach(function (series) {
            if (series.type === 'pie' && series.label) {
                series.label = normalizeToArray(series.label);
            }
            else if (lodash.isUndefined(series.label)) {
                series.label = [getDefaultLabelOption()];
            }
            else if (!series.label) {
                series.label = [];
            }
        });
    };
    PieSeriesModel.prototype.getDefaultOption = function (option) {
        return __assign(__assign({}, _super.prototype.getDefaultOption.call(this)), { type: 'pie', rose: false, name: '', center: ['50%', '50%'], size: '75%', innerSize: 0, encode: {
                name: '',
                value: '',
                color: null,
            }, startAngle: -90, endAngle: null, padAngle: 0, selectedOffset: 10, minSizeRatio: 0, hoverOffset: 10, selectedRotate: false, selectedRadiusOffset: 0, selectedMode: 'single', shapeStyle: {
                stroke: '#ffffff',
                lineWidth: 1,
                shadowBlur: 0,
                shadowOffsetX: 0,
                shadowOffsetY: 0,
                shadowColor: 'rgba(0, 51, 120, 0)',
            }, connectLine: {
                show: true,
                leadLength: 12,
                lineWidth: 1,
                stroke: '#6E748E',
            }, title: {
                show: true,
                offsetX: 0,
                offsetY: 0,
                text: '',
                color: '#333333',
                fontSize: 12,
                textAlign: 'center',
                textBaseline: 'middle',
            }, label: option.label
                .filter(function (val) { return val; })
                .map(function (labelOption) { return merge(getDefaultLabelOption(), labelOption); }), hover: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                    shadowBlur: 8,
                    shadowColor: 'rgba(0, 51, 120, .2)',
                },
            }, selected: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            }, visible: true, showLegend: true, animate: null });
    };
    PieSeriesModel.prototype.getInitialState = function () {
        return {
            cx: 0,
            cy: 0,
            radius: 0,
            innerRadius: 0,
            points: [],
            needAnimation: this.needAnimation(),
        };
    };
    // with pattern mode, no need animate fill attribute
    PieSeriesModel.prototype.getSliceStyle = function (dataIndex, omitFill) {
        if (omitFill === void 0) { omitFill = false; }
        var point = this.state.points[dataIndex];
        var _a = this.option, optionHoverOffset = _a.hoverOffset, selectedRadiusOffset = _a.selectedRadiusOffset, _b = _a.shapeStyle, fill = _b.fill, borderRadius = _b.borderRadius, cornerRadius = _b.cornerRadius;
        var hoverOffset = getPercentOrNumberValue(optionHoverOffset, this.state.radius);
        var handledPoint = this.handleAngleWithPadding(point);
        var translate = this.getPointOffset(handledPoint);
        var hover = this.state.hoverPointIndex === dataIndex;
        var cx = handledPoint.cx, cy = handledPoint.cy, radius = handledPoint.radius, innerRadius = handledPoint.innerRadius, startAngle = handledPoint.startAngle, endAngle = handledPoint.endAngle, selected = handledPoint.selected, visible = handledPoint.visible;
        var color = fill || this.getPointFillWithPattern(point, dataIndex);
        var currentRadius = radius;
        if (selected) {
            if (hover && visible && hoverOffset > selectedRadiusOffset) {
                currentRadius += hoverOffset;
            }
            else {
                currentRadius += selectedRadiusOffset;
            }
        }
        if (!selected && hover && visible) {
            currentRadius += hoverOffset;
        }
        var ret = __assign(__assign(__assign(__assign({ cx: cx,
            cy: cy, radius: currentRadius, radiusI: innerRadius, start: degToRad(startAngle), end: degToRad(endAngle), cursor: 'pointer', position: translate }, this.getShapeStyle(this.option.shapeStyle)), { borderRadius: cornerRadius || borderRadius }), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)), (hover ? this.getShapeStyle(this.option.hover.shapeStyle) : null));
        if (!omitFill) {
            ret.fill = hover ? this.getHoverColor(color) : color;
        }
        return ret;
    };
    PieSeriesModel.prototype.getPieSlices = function () {
        var _this = this;
        var _a = this.state, cx = _a.cx, cy = _a.cy, radius = _a.radius, innerRadius = _a.innerRadius;
        var _b = this.option, startAngle = _b.startAngle, pieEndAngle = _b.endAngle, minSizeRatio = _b.minSizeRatio;
        var endAngle = lodash.isNumber(pieEndAngle) ? pieEndAngle : startAngle + 360;
        var dataset = this.getDataset();
        var nameKey = this.option.encode.name;
        var valueKey = this.option.encode.value;
        var encoder = this.getEncoder();
        var dataCount = dataset.count();
        var points = [];
        var visibleDataCount = dataCount - this.invisibleItems.size;
        var dataList = dataset.getData();
        var visibleValueList = dataList
            .filter(function (item, index) { return !_this.invisibleItems.has(index); })
            .map(function (item) {
            var value = parseFloat(item[valueKey]);
            return isNaN(value) ? 0 : value;
        });
        var totalValue = lodash.sum(visibleValueList);
        var visiblePercentList = getPercentListWithRemainderMethod(visibleValueList, 2);
        var maxValue = lodash.max(visibleValueList);
        var showPercentList = getPercentListWithMinSizeRatio(visibleValueList, minSizeRatio);
        var visibleIndex = -1;
        dataList.reduce(function (prevEndAngle, record, dataIndex) {
            var visible = !_this.invisibleItems.has(dataIndex);
            var name = record[nameKey];
            if (visible) {
                visibleIndex++;
            }
            var value = parseFloat(record[valueKey]);
            if (isNaN(value)) {
                value = 0;
            }
            var percent = (visiblePercentList[visibleIndex] || 0).toFixed(2).replace('.00', '') + "%";
            var encode = encoder.getEncodeData(record, dataIndex);
            var sliceStartAngle = prevEndAngle;
            var sliceEndAngle;
            if (totalValue !== 0) {
                sliceEndAngle =
                    visible && !isNaN(value)
                        ? sliceStartAngle + showPercentList[visibleIndex] * (endAngle - startAngle)
                        : sliceStartAngle;
            }
            else if (visibleDataCount === 0 || !visible) {
                sliceEndAngle = sliceStartAngle;
            }
            else {
                var gap = (endAngle - startAngle) / visibleDataCount;
                sliceEndAngle = sliceStartAngle + gap;
            }
            var midAngle = (sliceStartAngle + sliceEndAngle) / 2;
            var sliceRadius = radius;
            if (_this.option.rose) {
                if (totalValue > 0) {
                    sliceRadius = innerRadius + (value / maxValue) * (radius - innerRadius);
                }
                else {
                    sliceRadius = radius;
                }
            }
            var angle = normalizeAngle(midAngle);
            var side = angle >= 90 && angle < 270 ? 'left' : 'right';
            points.push({
                side: side,
                encode: encode,
                cx: cx,
                cy: cy,
                radius: sliceRadius,
                innerRadius: innerRadius,
                startAngle: sliceStartAngle,
                endAngle: sliceEndAngle,
                midAngle: midAngle,
                name: name,
                value: value,
                percent: percent,
                color: encode.color,
                selected: _this.selectedItems.has(record),
                visible: visible,
                dataIndex: dataIndex,
                labelPosition: [],
                shapeRef: createRef(),
                labelRef: createRef(),
                lineRef: createRef(),
            });
            return sliceEndAngle;
        }, startAngle);
        var selectedPoint = lodash.find(points, function (point) { return point.selected; });
        if (selectedPoint && !(this.option.selectedMode === 'multiple') && this.option.selectedRotate) {
            var rotation_1 = -selectedPoint.midAngle % 360;
            if (Math.abs(rotation_1) > 180) {
                rotation_1 = rotation_1 > 0 ? rotation_1 - 360 : rotation_1 + 360;
            }
            points.forEach(function (point) {
                point.startAngle += rotation_1;
                point.midAngle += rotation_1;
                point.endAngle += rotation_1;
            });
        }
        return points;
    };
    PieSeriesModel.prototype.beforeRender = function () {
        var _this = this;
        var _a = this.option, center = _a.center, startAngle = _a.startAngle, pieEndAngle = _a.endAngle, label = _a.label;
        var endAngle = lodash.isNumber(pieEndAngle) ? pieEndAngle : startAngle + 360;
        var _b = this.getFacetBBox(), top = _b.top, left = _b.left, width = _b.width, height = _b.height;
        var radius = getPercentOrNumberValue(this.option.size, Math.min(width, height)) / 2;
        var innerRadius = getPercentOrNumberValue(this.option.innerSize, radius * 2) / 2;
        var cx = (isPercent(center[0]) ? left + getPercentOrNumberValue(center[0], width) : center[0]);
        var cy = (isPercent(center[1]) ? top + getPercentOrNumberValue(center[1], height) : center[1]);
        var state = {
            cx: cx,
            cy: cy,
            radius: radius,
            innerRadius: innerRadius,
            points: [],
        };
        this.setState(state);
        this.setState({
            points: this.getPieSlices(),
        });
        label.forEach(function (l, index) {
            if (l.position === 'outside' && l.distance > 0) {
                pieLabelLayout(_this, {
                    labelOption: l,
                    labelIndex: index,
                });
            }
        });
        this._clipPath = new Sector({
            cx: cx,
            cy: cy,
            radius: Math.max(this.chart.getWidth(), this.chart.getHeight()),
            radiusI: 0,
            start: degToRad(startAngle),
            end: !this.state.needAnimation ? degToRad(endAngle) : degToRad(startAngle),
        });
    };
    PieSeriesModel.prototype.afterRender = function () {
        var _this = this;
        this.option.label.forEach(function (labelOption) {
            if (labelOption.show && labelOption.autoHide) {
                _this.state.points.forEach(function (point) {
                    var _a, _b;
                    if (!((_a = point.labelRef.current) === null || _a === void 0 ? void 0 : _a.attr.display)) {
                        (_b = point.lineRef.current) === null || _b === void 0 ? void 0 : _b.setAttr('display', false);
                    }
                });
            }
        });
        if (!this.state.needAnimation || this.state.points.length === 0) {
            return;
        }
        var _a = this.option, startAngle = _a.startAngle, pieEndAngle = _a.endAngle;
        var endAngle = lodash.isNumber(pieEndAngle) ? pieEndAngle : startAngle + 360;
        this._clipPath.animateTo({
            start: degToRad(startAngle),
            end: degToRad(endAngle),
        });
        this.state.needAnimation = false;
    };
    PieSeriesModel.prototype.toggleHighlightPoint = function (dataIndex, active) {
        _super.prototype.toggleHighlightPoint.call(this, dataIndex, active);
        var point = this.state.points[dataIndex];
        if (!point) {
            return;
        }
        point.shapeRef.current.stopAllAnimation().animateTo(__assign({}, this.getSliceStyle(dataIndex, true)));
    };
    PieSeriesModel.prototype.toggleSelectPoint = function (dataIndex, selected) {
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var point = this.state.points[dataIndex];
        point.shapeRef.current.stopAllAnimation().animateTo(__assign({}, this.getSliceStyle(dataIndex, true)));
        if (this.option.selectedRotate) {
            this.chart.refresh();
        }
    };
    PieSeriesModel.prototype.toggleBlurPoint = function (point, blur) {
        var _a;
        _super.prototype.toggleBlurPoint.call(this, point, blur);
        (_a = point.lineRef.current) === null || _a === void 0 ? void 0 : _a.setAttr({
            opacity: blur ? 0.4 : 1,
        });
    };
    PieSeriesModel.prototype.getPointOffset = function (point) {
        var selectedOffset = this.option.selectedOffset;
        var midAngle = point.midAngle, selected = point.selected;
        var offsetX = selected ? selectedOffset * Math.cos(degToRad(midAngle)) : 0;
        var offsetY = selected ? selectedOffset * Math.sin(degToRad(midAngle)) : 0;
        return [offsetX, offsetY];
    };
    PieSeriesModel.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('pie-label'),
        });
        var label = this.option.label;
        label.forEach(function (labelOption, index) {
            var show = labelOption.show, position = labelOption.position, distance = labelOption.distance, offsetX = labelOption.offsetX, offsetY = labelOption.offsetY, alignTo = labelOption.alignTo, margin = labelOption.margin;
            var inside = position.indexOf('inside') !== -1;
            var labels = _this.state.points.map(function (point, dataIndex) {
                var labelText = _this.getDataLabel(dataIndex, labelOption);
                if (!labelText) {
                    return;
                }
                var side = point.side, visible = point.visible, labelPosition = point.labelPosition, labelWidth = point.labelWidth;
                var currentLabelPosition = labelPosition[index];
                var textPosition = getSectorLabelPosition(point, position, distance);
                var textSide = side;
                var labelDistanceToLine = labelOption.distanceToConnectLine;
                var x = textPosition.x, y = textPosition.y;
                if (currentLabelPosition) {
                    x =
                        textSide === 'left'
                            ? currentLabelPosition.x - labelDistanceToLine
                            : currentLabelPosition.x + labelDistanceToLine;
                    y = currentLabelPosition.y;
                }
                if (alignTo === 'edge' && position === 'outside') {
                    if (textSide === 'left') {
                        x = _this.getEdgePosition('left', margin, { width: labelWidth, height: 0 });
                    }
                    else if (textSide === 'right') {
                        x = _this.getEdgePosition('right', margin, { width: labelWidth, height: 0 });
                    }
                }
                x += offsetX;
                y += offsetY;
                return createRichText(__assign(__assign({ display: show && visible, ref: point.labelRef, text: labelText, fill: inside ? '#ffffff' : '#6E748E', x: x,
                    y: y, textAlign: inside ? textPosition.textAlign : textSide === 'left' ? 'right' : 'left', textBaseline: position === 'outside' ? 'middle' : textPosition.textBaseline, pointerEvents: inside ? 'none' : 'auto' }, _this.getTextStyle(labelOption)), { onEvent: function (event) {
                        return _this.handleUIEvent(event, 'label', {
                            point: _this.getWrappedPoint(dataIndex),
                        });
                    } }));
            });
            if (labelOption.autoHide) {
                _this.chart.__autoHideLabel(labels);
            }
            group.addAll(labels);
        });
        return group;
    };
    PieSeriesModel.prototype.renderConnectLine = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('pie-connect-line'),
        });
        var label = this.option.label;
        label.forEach(function (l, index) {
            var show = l.show, position = l.position, distance = l.distance, alignTo = l.alignTo, margin = l.margin, distanceToConnectLine = l.distanceToConnectLine;
            var leadLength = _this.option.connectLine.leadLength;
            if (position === 'outside' && show && _this.option.connectLine.show) {
                var connectLines = _this.state.points.map(function (point, dataIndex) {
                    var labelText = _this.getDataLabel(dataIndex, l);
                    if (!labelText) {
                        return;
                    }
                    var side = point.side, cx = point.cx, cy = point.cy, radius = point.radius, midAngle = point.midAngle, color = point.color, visible = point.visible;
                    var pointOnPolar = getPolar({ cx: cx, cy: cy });
                    var p1 = pointOnPolar(midAngle, radius);
                    var p2 = pointOnPolar(midAngle, radius + distance);
                    if (point.labelPosition[index]) {
                        p2 = point.labelPosition[index];
                    }
                    var py = p2.y;
                    var px = side === 'left' ? p2.x + leadLength : p2.x - leadLength;
                    if (alignTo === 'edge') {
                        p2.x =
                            side === 'left'
                                ? margin + point.labelWidth + distanceToConnectLine
                                : _this.chart.getWidth() - margin - point.labelWidth - distanceToConnectLine;
                    }
                    return new Polyline(__assign(__assign({ display: visible, ref: point.lineRef, pointList: [p2, { x: px, y: py }, p1], stroke: color }, _this.getLineStyle(_this.option.connectLine)), { onEvent: function (event) {
                            return _this.handleUIEvent(event, eventGraph.CONNECT_LINE, {
                                title: _this.option.name,
                                point: _this.getWrappedPoint(dataIndex),
                            });
                        } }));
                });
                group.addAll(connectLines);
            }
        });
        return group;
    };
    PieSeriesModel.prototype.renderTitle = function () {
        var _a = this.option.title, show = _a.show, offsetX = _a.offsetX, offsetY = _a.offsetY, text = _a.text;
        if (show) {
            var _b = this.state, cx = _b.cx, cy = _b.cy;
            var x = cx + offsetX;
            var y = cy + offsetY;
            return createRichText(__assign({ text: text,
                x: x,
                y: y }, this.getTextStyle(this.option.title)));
        }
    };
    PieSeriesModel.prototype.handleAngleWithPadding = function (slice) {
        var padAngle = this.option.padAngle;
        var padAngleHalf = padAngle / 2;
        var startAngle = slice.startAngle, endAngle = slice.endAngle;
        var startAngleWithPadding = startAngle;
        var endAngleWithPadding = endAngle;
        if (Math.abs(endAngle - startAngle) > padAngle) {
            var offsetAngle = endAngle > startAngle ? padAngleHalf : -padAngleHalf;
            startAngleWithPadding += offsetAngle;
            endAngleWithPadding -= offsetAngle;
        }
        return __assign(__assign({}, slice), { startAngle: startAngleWithPadding, endAngle: endAngleWithPadding });
    };
    PieSeriesModel.prototype.renderSlices = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('pie-slice'),
        });
        this.state.points.forEach(function (point, dataIndex) {
            var sector = new Sector(__assign(__assign({ ref: point.shapeRef }, _this.getSliceStyle(dataIndex)), { onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: _this.option.name,
                        point: _this.getWrappedPoint(dataIndex),
                    });
                } }));
            group.add(sector);
        });
        return group;
    };
    PieSeriesModel.prototype.render = function () {
        var group = new Group({
            key: this.getKey('pie'),
            clip: this._clipPath,
        });
        if (!this.visible()) {
            return group;
        }
        group.add(this.renderConnectLine());
        group.add(this.renderSlices());
        group.add(this.renderLabels());
        group.add(this.renderTitle());
        return group;
    };
    PieSeriesModel.dependencies = ['legend'];
    return PieSeriesModel;
}(SeriesModel));
export default PieSeriesModel;
registerChart('pie', PieSeriesModel);
